// src/components/FormLists/formListsAdapters.js

/**
 * formListsAdapters
 * -----------------------------------------------------------------------------
 * Pure adapter helpers for converting the existing Form Lists schema into the
 * shared grouped spreadsheet shape, and converting edited groups back into the
 * existing form-local dropdown option schema.
 *
 * EXISTING FORM LISTS SCHEMA
 *  fieldsMetadata[fieldId] = {
 *    id,
 *    type: 'single-select' | 'multi-select',
 *    label,
 *    optionsSetUFI,
 *    ...
 *  }
 *
 *  dropdownOptions[optionsSetUFI] = [
 *    {
 *      optionId,
 *      label,
 *      value,
 *      hint,
 *      dep,
 *      order,
 *      systemValue,
 *    }
 *  ]
 *
 *  dropdownDependencies[optionsSetUFI][optionId] = {
 *    dependentOptionIds: []
 *  }
 *
 * PURPOSE
 *  • Keep the shared Spreadsheet/ engine domain-free
 *  • Preserve existing form-local dropdown schema
 *  • Preserve existing optionId identity
 *  • Preserve optionsSetUFI as the option-set persistence key
 *  • Preserve org-managed governance flags for read-only handling
 *  • Avoid SmartLists managed-field / managed-option-set assumptions
 *
 * IMPORTANT
 *  • This file does NOT read/write React context.
 *  • This file does NOT import Firebase.
 *  • This file does NOT save dropdown options.
 *  • This file does NOT save dropdown dependencies.
 *  • This file does NOT mutate the input objects.
 * -----------------------------------------------------------------------------
 */

import { v4 as uuidv4 } from 'uuid';

import {
  isOrgManagedDropdownInstance,
} from '../../utils/managedFieldInstances';

import {
  FORM_LISTS_COLUMN_CONFIG,
  FORM_LISTS_GROUP_ID_KEY,
  FORM_LISTS_ROW_ID_KEY,
  FORM_LISTS_ROW_KEY,
} from './formListsGridConfig';

import {
  normalizeString,
  toArraySafe,
} from '../Spreadsheet/groupedSpreadsheetUtils';

export const FORM_LISTS_SUPPORTED_FIELD_TYPES = Object.freeze([
  'single-select',
  'multi-select',
]);

const SYNTHETIC_OPTION_LABELS = Object.freeze([
  'other',
  '__other__',
]);

/* -------------------------------------------------------------------------- */
/* Basic helpers                                                              */
/* -------------------------------------------------------------------------- */

export const isFormListDropdownField = (fieldMetadata) => {
  return (
    !!fieldMetadata &&
    typeof fieldMetadata === 'object' &&
    FORM_LISTS_SUPPORTED_FIELD_TYPES.includes(fieldMetadata?.type) &&
    normalizeString(fieldMetadata?.optionsSetUFI, '').length > 0
  );
};

export const getFormListFieldId = (fieldIdFromMap, fieldMetadata = {}) => {
  return normalizeString(
    fieldMetadata?.id ||
      fieldMetadata?.fieldId ||
      fieldIdFromMap,
    '',
  );
};

export const getFormListGroupId = ({
  fieldId,
  optionsSetUFI,
} = {}) => {
  const safeFieldId = normalizeString(fieldId, '');
  const safeOptionsSetUFI = normalizeString(optionsSetUFI, '');

  if (safeFieldId && safeOptionsSetUFI) {
    return `${safeFieldId}__${safeOptionsSetUFI}`;
  }

  return safeFieldId || safeOptionsSetUFI;
};

export const parseFormListGroupId = (groupId = '') => {
  const normalized = normalizeString(groupId, '');
  const [fieldId = '', optionsSetUFI = ''] = normalized.split('__');

  return {
    fieldId,
    optionsSetUFI,
  };
};

export const getFormListRowId = ({
  groupId,
  optionId,
  rowIndex,
} = {}) => {
  const safeGroupId = normalizeString(groupId, 'group');
  const safeOptionId = normalizeString(optionId, '');

  if (safeOptionId) {
    return `${safeGroupId}__option__${safeOptionId}`;
  }

  const safeIndex = Number.isFinite(Number(rowIndex))
    ? Number(rowIndex)
    : 0;

  return `${safeGroupId}__row__${safeIndex}`;
};

export const normalizeFormListOrder = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

export const normalizeFormListLabel = (value) => (
  value == null ? '' : String(value).trim()
);

export const normalizeFormListStringValue = (value) => (
  value == null ? '' : String(value)
);

export const isSyntheticFormListOptionLabel = (label) => {
  const normalized = normalizeFormListLabel(label).toLowerCase();
  return SYNTHETIC_OPTION_LABELS.includes(normalized);
};

/* -------------------------------------------------------------------------- */
/* Field / group normalization                                                */
/* -------------------------------------------------------------------------- */

export const normalizeFormListFieldForGroup = ({
  fieldId,
  fieldMetadata,
} = {}) => {
  const safeFieldId = getFormListFieldId(fieldId, fieldMetadata);
  const optionsSetUFI = normalizeString(fieldMetadata?.optionsSetUFI, '');

  const managedFieldId = normalizeString(fieldMetadata?.managedFieldId, '');

  const managedOptionSetId = normalizeString(
    fieldMetadata?.managedOptionSetId ||
      fieldMetadata?.optionsSource?.optionsSetId,
    '',
  );

  const optionsSource =
    normalizeString(fieldMetadata?.optionsSource, '') ||
    normalizeString(fieldMetadata?.optionsSource?.type, '');

  /**
   * Org-managed field detection
   * ---------------------------------------------------------------------------
   * Prefer the canonical managed-field helper, but keep a defensive fallback
   * for transitional metadata shapes created during the SmartLists rollout.
   *
   * WHY
   *  • FormListsLab must render SmartList-backed option values as read-only.
   *  • The strict helper is still the canonical path.
   *  • The fallback exists only when explicit managed identity is present:
   *      - managedFieldId
   *      - managedOptionSetId / optionsSource.optionsSetId
   *      - organization/managed option source marker
   *
   * ENTERPRISE RULE
   *  • Do not lock normal local dropdowns just because they have optionsSetUFI.
   *  • Only treat a group as org-managed when explicit managed identity exists.
   */
  const isOrgManaged =
    isOrgManagedDropdownInstance(fieldMetadata) ||
    (
      fieldMetadata?.orgManaged === true &&
      Boolean(managedFieldId) &&
      Boolean(managedOptionSetId)
    ) ||
    (
      Boolean(managedFieldId) &&
      Boolean(managedOptionSetId) &&
      (
        optionsSource === 'organization' ||
        optionsSource === 'managedOptionSet'
      )
    );

  const governance = fieldMetadata?.governance || {};

  const allowLocalOptionEditing =
    !isOrgManaged ||
    governance?.allowLocalOptionEditing === true ||
    fieldMetadata?.allowLocalOptionEditing === true;

  const allowLocalLabelOverride =
    !isOrgManaged ||
    governance?.allowLocalLabelOverride === true ||
    fieldMetadata?.allowLocalLabelOverride === true;

  /**
   * ENTERPRISE POLICY FOR LISTS LAB
   * ---------------------------------------------------------------------------
   * Dependencies remain FORM-SPECIFIC in the current working architecture.
   *
   * DropdownDependenciesContext already supports dependency graphs involving:
   *  • local form dropdown instances
   *  • org-managed dropdown field instances
   *
   * SmartLists itself has no org-wide dependency model. However, when an
   * org-managed SmartList field is inserted into a form, the dependency rule
   * belongs to the FORM, not to SmartLists.
   *
   * For that reason, Lists Lab intentionally does NOT enforce
   * governance.allowLocalDependencies yet. Enforcing it now would be a behavior
   * change that could block dependency editing for org-managed field instances
   * because SmartLists defaults may set allowLocalDependencies to false.
   *
   * Future hardening option:
   *  • show a warning or lock the dependency cell only after the product
   *    governance rule is intentionally changed.
   *
   * Current V2 Lab rule:
   *  • keep dependencies available for all dropdown groups
   *  • continue storing dependency rules through DropdownDependenciesContext
   *  • do not persist dependencies into SmartLists
   */
  const allowLocalDependencies = true;

  return {
    fieldId: safeFieldId,
    fieldType: fieldMetadata?.type || '',
    title:
      normalizeString(fieldMetadata?.label, '') ||
      normalizeString(fieldMetadata?.name, '') ||
      'Untitled list',
    optionsSetUFI,
    isOrgManaged,
    allowLocalOptionEditing,
    allowLocalLabelOverride,
    allowLocalDependencies,
    managedFieldId: managedFieldId || null,
    managedOptionSetId: managedOptionSetId || null,
    optionsSource: optionsSource || null,
    fieldMetadata: {
      ...fieldMetadata,
      id: safeFieldId,
      optionsSetUFI,
      managedFieldId: managedFieldId || fieldMetadata?.managedFieldId || null,
      managedOptionSetId:
        managedOptionSetId ||
        fieldMetadata?.managedOptionSetId ||
        fieldMetadata?.optionsSource?.optionsSetId ||
        null,
      optionsSource:
        optionsSource ||
        fieldMetadata?.optionsSource ||
        null,
    },
  };
};

export const sortFormListFields = (fields = []) => {
  return [...fields].sort((a, b) => {
    const aOrder = Number(a?.order);
    const bOrder = Number(b?.order);

    const aHasOrder = Number.isFinite(aOrder);
    const bHasOrder = Number.isFinite(bOrder);

    if (aHasOrder && bHasOrder) return aOrder - bOrder;
    if (aHasOrder) return -1;
    if (bHasOrder) return 1;

    return normalizeString(a?.label, '').localeCompare(
      normalizeString(b?.label, ''),
    );
  });
};

/* -------------------------------------------------------------------------- */
/* Option row normalization                                                   */
/* -------------------------------------------------------------------------- */

export const normalizeFormListOptionToRow = ({
  option = {},
  groupId,
  rowIndex = 0,
} = {}) => {
  const optionId = normalizeString(option?.optionId, '');
  const label = normalizeFormListLabel(
    option?.label ?? option?.options ?? '',
  );

  const rowId = getFormListRowId({
    groupId,
    optionId,
    rowIndex,
  });

  return {
    [FORM_LISTS_ROW_ID_KEY]: rowId,
    optionId,
    label,
    value: normalizeFormListStringValue(option?.value ?? ''),
    hint: normalizeFormListStringValue(option?.hint ?? ''),
    dep: normalizeFormListStringValue(option?.dep ?? ''),
    order: normalizeFormListOrder(option?.order, rowIndex),

    // Approved hidden system metadata.
    //
    // IMPORTANT:
    // This is not rendered as a spreadsheet column and is not user-editable here.
    // It is preserved so system-backed dropdowns such as Asset Type keep their
    // durable machine meaning when labels/hints are edited in Lists.
    systemValue: normalizeFormListStringValue(option?.systemValue ?? ''),

    // The dependency column is custom UI-only. Dependency rules are stored in
    // DropdownDependenciesContext by optionsSetUFI/optionId, not here.
    dependency: null,

    // Local diagnostics / adapter flags. These are not persisted.
    isNew: false,
    dirty: false,
  };
};

export const buildFormListOptionRows = ({
  options = [],
  groupId,
} = {}) => {
  return toArraySafe(options)
    .map((option, rowIndex) => normalizeFormListOptionToRow({
      option,
      groupId,
      rowIndex,
    }))
    .sort((a, b) => {
      const aOrder = normalizeFormListOrder(a?.order, 0);
      const bOrder = normalizeFormListOrder(b?.order, 0);

      return aOrder - bOrder;
    });
};

/* -------------------------------------------------------------------------- */
/* Context → spreadsheet groups                                               */
/* -------------------------------------------------------------------------- */

export const buildFormListGroups = ({
  fieldsMetadata = {},
  dropdownOptions = {},
  includeOrgManaged = true,
} = {}) => {
  const entries = Object.entries(fieldsMetadata || {});

  const dropdownFields = entries
    .map(([fieldIdFromMap, fieldMetadata]) => {
      if (!isFormListDropdownField(fieldMetadata)) return null;

      const fieldId = getFormListFieldId(fieldIdFromMap, fieldMetadata);

      return {
        ...fieldMetadata,
        id: fieldId,
      };
    })
    .filter(Boolean)
    .filter((fieldMetadata) => {
      if (includeOrgManaged) return true;
      return !isOrgManagedDropdownInstance(fieldMetadata);
    });

  return sortFormListFields(dropdownFields).map((fieldMetadata) => {
    const normalizedField = normalizeFormListFieldForGroup({
      fieldId: fieldMetadata.id,
      fieldMetadata,
    });

    const groupId = getFormListGroupId({
      fieldId: normalizedField.fieldId,
      optionsSetUFI: normalizedField.optionsSetUFI,
    });

    const optionsForSet =
      dropdownOptions?.[normalizedField.optionsSetUFI] || [];

    const optionRows = buildFormListOptionRows({
      options: optionsForSet,
      groupId,
    });

    return {
      [FORM_LISTS_GROUP_ID_KEY]: groupId,
      fieldId: normalizedField.fieldId,
      optionsSetUFI: normalizedField.optionsSetUFI,
      title: normalizedField.title,
      fieldType: normalizedField.fieldType,

      // Ownership / governance flags consumed by FormLists V2.
      isOrgManaged: normalizedField.isOrgManaged,
      allowLocalOptionEditing: normalizedField.allowLocalOptionEditing,
      allowLocalLabelOverride: normalizedField.allowLocalLabelOverride,
      allowLocalDependencies: normalizedField.allowLocalDependencies,

      managedFieldId: normalizedField.managedFieldId,
      managedOptionSetId: normalizedField.managedOptionSetId,
      optionsSource: normalizedField.optionsSource,

      columns: FORM_LISTS_COLUMN_CONFIG,
      [FORM_LISTS_ROW_KEY]: optionRows,

      // Keep the raw/normalized field metadata available for adapters and
      // diagnostics without forcing the shared Spreadsheet/ engine to know it.
      fieldMetadata: normalizedField.fieldMetadata,
    };
  });
};

export const buildFormListGroupIndex = (groups = []) => {
  return toArraySafe(groups).reduce((acc, group) => {
    const groupId = normalizeString(group?.[FORM_LISTS_GROUP_ID_KEY], '');
    if (!groupId) return acc;

    acc[groupId] = group;
    return acc;
  }, {});
};

export const getFormListGroupById = (groups = [], groupId) => {
  const safeGroupId = normalizeString(groupId, '');

  return toArraySafe(groups).find(
    (group) =>
      normalizeString(group?.[FORM_LISTS_GROUP_ID_KEY], '') === safeGroupId,
  ) || null;
};

/* -------------------------------------------------------------------------- */
/* Spreadsheet groups → dropdownOptions schema                                */
/* -------------------------------------------------------------------------- */

export const isMeaningfulFormListOptionRow = (row = {}) => {
  const label = normalizeFormListLabel(row?.label);

  if (!label) return false;
  if (isSyntheticFormListOptionLabel(label)) return false;

  return true;
};

/**
 * ensureFormListOptionRowIdentity
 * -----------------------------------------------------------------------------
 * Assigns a stable Form Lists optionId as soon as a spreadsheet row becomes a
 * meaningful dropdown option row.
 *
 * WHY THIS EXISTS
 *  • The shared Spreadsheet/ engine intentionally does not know about optionId.
 *  • Form Lists dependencies are keyed by:
 *      dropdownDependencies[optionsSetUFI][optionId]
 *  • The dependency icon must not open until an option row has a stable optionId.
 *  • Existing DropdownSyncLogic generated optionId during local row editing.
 *  • V2 Lists Lab must preserve that behavior in the Form Lists adapter layer,
 *    not inside the generic spreadsheet engine.
 *
 * IMPORTANT
 *  • This is NOT a temporary ID.
 *  • This ID becomes the real local option identity for this option row.
 *  • Later apply/autosave preserves row.optionId when building dropdownOptions.
 *  • We intentionally do NOT regenerate rowId here because the spreadsheet
 *    canvas uses rowId for active selection/navigation during the current
 *    edit session.
 */
export const ensureFormListOptionRowIdentity = ({
  row = {},
  group = {},
  groupId = '',
  rowIndex = 0,
} = {}) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  if (!isMeaningfulFormListOptionRow(row)) {
    return row;
  }

  const existingOptionId = normalizeString(row?.optionId, '');

  if (existingOptionId) {
    return row;
  }

  const nextOptionId = uuidv4();

  return {
    ...row,

    // Stable Form Lists option identity used by dependencies and persistence.
    optionId: nextOptionId,

    // Preserve existing spreadsheet rowId. Do not rewrite selection identity
    // during the active edit session.
    [FORM_LISTS_ROW_ID_KEY]: row?.[FORM_LISTS_ROW_ID_KEY],

    // Normalize the rest of the persisted dropdown option shape so new rows
    // behave like rows created through the existing Form Lists path.
    value: normalizeFormListStringValue(row?.value ?? ''),
    hint: normalizeFormListStringValue(row?.hint ?? ''),
    dep: normalizeFormListStringValue(row?.dep ?? ''),
    order: normalizeFormListOrder(row?.order, rowIndex),

    // Approved hidden system metadata.
    //
    // Existing rows can carry systemValue from dropdownOptions. New local rows
    // normally will not have it, which is correct because normal user-created
    // options are not system-backed.
    systemValue: normalizeFormListStringValue(row?.systemValue ?? ''),

    dependency: row?.dependency ?? null,

    // Local adapter diagnostic flag. This is not required for persistence,
    // but it keeps the row inspectable during V2 testing.
    isNew: row?.isNew === false ? false : true,

    // Keep existing dirty flag if present. The shared canvas still owns the
    // canonical dirty state separately.
    dirty: row?.dirty === true,
  };
};

export const normalizeFormListRowForDropdownOption = ({
  row = {},
  order = 0,
  generateOptionIdIfMissing = true,
} = {}) => {
  if (!isMeaningfulFormListOptionRow(row)) {
    return null;
  }

  const existingOptionId = normalizeString(row?.optionId, '');
  const optionId =
    existingOptionId ||
    (generateOptionIdIfMissing ? uuidv4() : '');

  if (!optionId) return null;

  const systemValue = normalizeFormListStringValue(row?.systemValue ?? '');

  const nextOption = {
    optionId,
    label: normalizeFormListLabel(row?.label),
    value: normalizeFormListStringValue(row?.value ?? ''),
    hint: normalizeFormListStringValue(row?.hint ?? ''),
    dep: normalizeFormListStringValue(row?.dep ?? ''),

    // Preserve current Form Lists behavior: order is zero-based because the
    // existing handleSaveDropdownOptions(...) writes order: index.
    order,
  };

  // ENTERPRISE:
  // Preserve approved option-level system identity when present.
  //
  // Do not infer systemValue from labels here. Form Lists is an editing surface,
  // not the owner of system semantics. Schema/default creation and backend
  // catalog resolution own that responsibility.
  if (systemValue) {
    nextOption.systemValue = systemValue;
  }

  return nextOption;
};

export const buildDropdownOptionsForFormListGroup = ({
  group,
  generateOptionIdIfMissing = true,
} = {}) => {
  const rows = toArraySafe(group?.[FORM_LISTS_ROW_KEY]);

  return rows
    .map((row, index) => normalizeFormListRowForDropdownOption({
      row,
      order: index,
      generateOptionIdIfMissing,
    }))
    .filter(Boolean)
    .map((option, index) => ({
      ...option,
      order: index,
    }));
};

export const buildDropdownOptionsMapFromFormListGroups = ({
  groups = [],
  includeReadOnlyGroups = false,
  generateOptionIdIfMissing = true,
} = {}) => {
  return toArraySafe(groups).reduce((acc, group) => {
    const optionsSetUFI = normalizeString(group?.optionsSetUFI, '');

    if (!optionsSetUFI) return acc;

    const isReadOnlyManagedGroup =
      group?.isOrgManaged === true &&
      group?.allowLocalOptionEditing !== true;

    if (isReadOnlyManagedGroup && !includeReadOnlyGroups) {
      return acc;
    }

    acc[optionsSetUFI] = buildDropdownOptionsForFormListGroup({
      group,
      generateOptionIdIfMissing,
    });

    return acc;
  }, {});
};

export const buildFormListOptionWrites = ({
  groups = [],
  dirtyGroupIds = null,
  includeReadOnlyGroups = false,
} = {}) => {
  const dirtySet =
    dirtyGroupIds instanceof Set
      ? dirtyGroupIds
      : Array.isArray(dirtyGroupIds)
        ? new Set(dirtyGroupIds)
        : null;

  return toArraySafe(groups).reduce((writes, group) => {
    const groupId = normalizeString(group?.[FORM_LISTS_GROUP_ID_KEY], '');
    const optionsSetUFI = normalizeString(group?.optionsSetUFI, '');

    if (!groupId || !optionsSetUFI) return writes;

    if (dirtySet && !dirtySet.has(groupId)) {
      return writes;
    }

    const isReadOnlyManagedGroup =
      group?.isOrgManaged === true &&
      group?.allowLocalOptionEditing !== true;

    if (isReadOnlyManagedGroup && !includeReadOnlyGroups) {
      return writes;
    }

    writes.push({
      groupId,
      fieldId: group?.fieldId || null,
      optionsSetUFI,
      nextOptions: buildDropdownOptionsForFormListGroup({
        group,
        generateOptionIdIfMissing: true,
      }),
    });

    return writes;
  }, []);
};

/* -------------------------------------------------------------------------- */
/* Diagnostics                                                                */
/* -------------------------------------------------------------------------- */

export const buildFormListsAdapterDiagnostics = ({
  fieldsMetadata = {},
  dropdownOptions = {},
  groups = [],
} = {}) => {
  const dropdownFieldCount = Object.values(fieldsMetadata || {})
    .filter((field) => isFormListDropdownField(field))
    .length;

  const optionSetIdsFromFields = new Set(
    Object.values(fieldsMetadata || {})
      .filter((field) => isFormListDropdownField(field))
      .map((field) => normalizeString(field?.optionsSetUFI, ''))
      .filter(Boolean),
  );

  const optionSetIdsFromOptions = Object.keys(dropdownOptions || {});

  const missingOptionSets = Array.from(optionSetIdsFromFields)
    .filter((optionsSetUFI) => (
      !optionSetIdsFromOptions.includes(optionsSetUFI)
    ));

  const orgManagedGroupCount = toArraySafe(groups)
    .filter((group) => group?.isOrgManaged === true)
    .length;

  const readOnlyGroupCount = toArraySafe(groups)
    .filter(
      (group) =>
        group?.isOrgManaged === true &&
        group?.allowLocalOptionEditing !== true,
    )
    .length;

  return {
    dropdownFieldCount,
    optionSetCount: optionSetIdsFromOptions.length,
    groupCount: toArraySafe(groups).length,
    missingOptionSets,
    orgManagedGroupCount,
    readOnlyGroupCount,
  };
};